import { useCallback, useMemo, useState } from "react";
import React from "react";
import { useNavigate, useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import {
  DeleteOutlineOutlinedIcon,
  ModeEditOutlineOutlinedIcon,
  SaveOutlinedIcon,
} from "icons";
import { colors } from "theme/palette";
import { format, FORMATS, parseJSON } from "utils/datetime";
import { downloadFileWithFileName } from "utils/file";
import { handleInvalidRequest } from "utils/sdk";
import { addQueryParams, reverse } from "utils/urls";

import Button from "components/Button";
import MuiDivider from "components/Divider";
import IconButton from "components/IconButton";
import { LinearProgress } from "components/Loading";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { useDealChecklistPermission } from "entities/Deal/sdk";
import SnapshotCreateDialog from "entities/Package/components/SnapshotCreateDialog";
import SnapshotRenameDialog from "entities/Package/components/SnapshotRenameDialog";
import { DueDiligenceTabChoices } from "entities/Package/components/Tabs/constants";
import { ChecklistSnapshotFileStatus } from "entities/Package/constants";
import {
  companyPackageSnapshotDelete,
  ICompanyPackageSnapshot,
  snapshotPDFFileSignedURLRetrieve,
  snapshotZIPFileSignedURLRetrieve,
  useCompanyPackageSnapshotList,
} from "entities/Package/sdk";

export interface ISavedSnapshots {
  showDeleteAndRenameButtons: boolean;
}

const SavedSnapshots = ({ showDeleteAndRenameButtons }: ISavedSnapshots) => {
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);

  const [currentSnapshotToEdit, setCurrentSnapshotToEdit] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const dealId = useMemo(() => Number(params.dealId), [params.dealId]);
  const companyPackageId = useMemo(
    () => Number(params.packageId),
    [params.packageId]
  );

  const navigate = useNavigate();

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const {
    data: snapshots,
    isLoading,
    mutate: mutateSnapshots,
  } = useCompanyPackageSnapshotList({
    dealId,
    companyPackageId,
  });

  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId: companyPackageId,
  });

  const constRenameDialogOpenHandle = useCallback(
    ({ id, name }: { id: number; name: string }) => {
      setCurrentSnapshotToEdit({ id, name });
    },
    [setCurrentSnapshotToEdit]
  );

  const constRenameDialogCloseHandle = useCallback(() => {
    setCurrentSnapshotToEdit(null);
  }, [setCurrentSnapshotToEdit]);

  const constCreateDialogOpenHandle = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  const constCreateDialogCloseHandle = useCallback(() => {
    setIsCreateDialogOpen(false);
  }, [setIsCreateDialogOpen]);

  const onUpdate = useCallback(() => mutateSnapshots(), [mutateSnapshots]);

  const onCreate = useCallback(
    ({ snapshotName }: { snapshotName: string }) => {
      constCreateDialogCloseHandle();
      navigate(
        addQueryParams(
          reverse(URLS.PRINTABLE_CHECKLIST, {
            dealId,
            packageId: companyPackageId,
          }),
          {
            snapshotName,
            tab: DueDiligenceTabChoices.SAVED_SNAPSHOTS,
          }
        )
      );
    },
    [constCreateDialogCloseHandle, companyPackageId, dealId, navigate]
  );

  const handleDelete = useCallback(
    ({ snapshotId }: { snapshotId: number }) => {
      showConfirmationDialog({
        onConfirm: () =>
          companyPackageSnapshotDelete({
            dealId,
            companyPackageId,
            snapshotId,
          })
            .then(() => {
              onUpdate();
              toast.successMessage("Snapshot has been successfully deleted!");
            })
            .catch(handleInvalidRequest),
        confirmButtonText: "Delete",
        message: "Are you sure you want to delete this snapshot?",
      });
    },
    [showConfirmationDialog, companyPackageId, dealId, onUpdate]
  );

  const downloadSnapshotPDF = useCallback(
    ({
      dealId,
      companyPackageId,
      snapshotId,
      fileName,
    }: {
      dealId: number;
      companyPackageId: number;
      snapshotId: number;
      fileName: string;
    }) => {
      const toastId = toast.loading("Retrieving file...");

      snapshotPDFFileSignedURLRetrieve({
        dealId,
        companyPackageId,
        snapshotId,
      })
        .then((url) =>
          downloadFileWithFileName({
            toastId,
            fileUrl: url,
            fileName,
          })
        )
        .catch(handleInvalidRequest);
    },
    []
  );

  const downloadSnapshotZIP = useCallback(
    ({
      dealId,
      companyPackageId,
      zipId,
      zipName,
    }: {
      dealId: number;
      companyPackageId: number;
      zipId: number;
      zipName: string;
    }) => {
      const toastId = toast.loading("Retrieving file...");

      snapshotZIPFileSignedURLRetrieve({
        dealId,
        companyPackageId,
        zipId,
      })
        .then((url) =>
          downloadFileWithFileName({
            toastId,
            fileUrl: url,
            fileName: zipName,
          })
        )
        .catch(handleInvalidRequest);
    },
    []
  );

  const downloadPDFTooltip = useCallback(
    ({ snapshot }: { snapshot: ICompanyPackageSnapshot }) => {
      if (
        snapshot?.pdf_file_status === ChecklistSnapshotFileStatus.IN_PROGRESS
      ) {
        return "Please wait while we generate the pdf file.";
      }
      if (snapshot?.pdf_file_status === ChecklistSnapshotFileStatus.FAILED) {
        return "The pdf generation has been unsuccessful.";
      }
      return undefined;
    },
    []
  );

  const downloadZIPTooltip = useCallback(
    ({ snapshot }: { snapshot: ICompanyPackageSnapshot }) => {
      if (
        snapshot?.zip_file_status === ChecklistSnapshotFileStatus.IN_PROGRESS
      ) {
        return "Please wait while we generate the zip file.";
      }
      if (snapshot?.zip_file_status === ChecklistSnapshotFileStatus.FAILED) {
        return "The zip generation has been unsuccessful.";
      }
      return undefined;
    },
    []
  );

  const disablePDFButton = useCallback(
    ({ snapshot }: { snapshot: ICompanyPackageSnapshot }) =>
      snapshot?.pdf_file_status === ChecklistSnapshotFileStatus.IN_PROGRESS ||
      snapshot?.pdf_file_status === ChecklistSnapshotFileStatus.FAILED,
    []
  );

  const disableZIPButton = useCallback(
    ({ snapshot }: { snapshot: ICompanyPackageSnapshot }) =>
      snapshot?.zip_file_status === ChecklistSnapshotFileStatus.IN_PROGRESS ||
      snapshot?.zip_file_status === ChecklistSnapshotFileStatus.FAILED,
    []
  );

  return (
    <>
      <Stack
        direction="row"
        gap={10}
        justifyContent="space-between"
        sx={{
          borderRadius: "4px",
          background: colors.blue10,
          padding: 2,
        }}
      >
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            gap={1}
          >
            <SaveOutlinedIcon width={24} height={24} />
            <Text variant="h3" fontWeight={700}>
              Saved snapshots
            </Text>
          </Stack>
          <Text variant="text3" color={colors.gray80}>
            Snapshots are past versions of your checklist with all input values
            and statuses saved and frozen at a certain point of time. You can
            save a snapshotted version of a checklist at any time. Later, you
            can access or download it for reference. Restoring your checklist to
            an earlier version of it is not supported.
          </Text>
        </Stack>
        <Button
          sx={{
            minWidth: "fit-content",
            alignSelf: "center",
          }}
          onClick={constCreateDialogOpenHandle}
        >
          <Text variant="text2" fontWeight={500}>
            + Create new snapshot
          </Text>
        </Button>
      </Stack>
      {isLoading && <LinearProgress sx={{ position: "sticky", left: 0 }} />}

      <Stack gap={2} marginTop={4}>
        {!_.isNil(snapshots) && snapshots.length > 0 && (
          <>
            <Stack display="grid" gridTemplateColumns="1fr 2fr" gap={1}>
              <Text variant="labelSmall" color={colors.gray40}>
                saved snapshot
              </Text>
              <Text variant="labelSmall" color={colors.gray40}>
                timestamp
              </Text>
            </Stack>
            <MuiDivider />
          </>
        )}
        {(_.isNil(snapshots) || snapshots.length === 0) && !isLoading && (
          <Text variant="text1">
            Currently there are no snapshots connected to this checklist.
          </Text>
        )}

        {isLoading &&
          _.range(4).map((index) => (
            <React.Fragment key={index}>
              <Stack
                display="grid"
                gridTemplateColumns="repeat(3, 1fr)"
                gap={1}
              >
                <Stack>
                  <Skeleton variant="text" width="50%" height={22} />
                </Stack>
                <Stack>
                  <Skeleton variant="text" width="70%" height={22} />
                </Stack>
                <Stack>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={24}
                    sx={{ alignSelf: "flex-end" }}
                  />
                </Stack>
              </Stack>
              <MuiDivider />
            </React.Fragment>
          ))}

        <Stack
          sx={{
            rowGap: 2,
            columnGap: 2,
            display: "grid",
            alignItems: "center",
            gridTemplateColumns: "repeat(3, 1fr)",
          }}
        >
          {snapshots?.map((snapshot) => (
            <React.Fragment key={snapshot.id}>
              <Text noWrap variant="text2" fontWeight={700}>
                {snapshot.name}
              </Text>
              <Text variant="text4" noWrap>
                {format(
                  parseJSON(snapshot.created_at),
                  FORMATS.FRONTEND.DATETIME
                )}
                , saved by {snapshot.created_by.first_name}{" "}
                {snapshot.created_by.last_name}
              </Text>
              <Stack justifyContent="flex-end" gap={1} direction="row">
                <Tooltip title={downloadPDFTooltip({ snapshot })}>
                  <span
                    style={{
                      alignSelf: "center",
                      cursor:
                        snapshot.pdf_file_status ===
                        ChecklistSnapshotFileStatus.FAILED
                          ? "not-allowed"
                          : "inherit",
                    }}
                  >
                    <Button
                      onClick={() =>
                        !_.isNil(snapshot.pdf_file) &&
                        downloadSnapshotPDF({
                          dealId,
                          companyPackageId,
                          snapshotId: snapshot.id,
                          fileName: snapshot.pdf_file.original_file_name,
                        })
                      }
                      sx={{
                        alignSelf: "center",
                        height: "max-content",
                        minHeight: "24px",
                        color: !disablePDFButton({ snapshot })
                          ? colors.black
                          : colors.gray20,
                      }}
                      variant="outlined"
                      disabled={disablePDFButton({ snapshot })}
                    >
                      <Text variant="text4" fontWeight={500}>
                        Download PDF
                      </Text>
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={downloadZIPTooltip({ snapshot })}>
                  <span
                    style={{
                      alignSelf: "center",
                      cursor:
                        snapshot.zip_file_status ===
                        ChecklistSnapshotFileStatus.FAILED
                          ? "not-allowed"
                          : "inherit",
                    }}
                  >
                    <Button
                      onClick={() =>
                        !_.isNil(snapshot.dd_zip_file) &&
                        downloadSnapshotZIP({
                          dealId,
                          companyPackageId,
                          zipId: snapshot.dd_zip_file.id,
                          zipName: snapshot.dd_zip_file.file.original_file_name,
                        })
                      }
                      sx={{
                        height: "max-content",
                        minHeight: "24px",
                        color: !disableZIPButton({ snapshot })
                          ? colors.black
                          : colors.gray20,
                      }}
                      variant="outlined"
                      disabled={disableZIPButton({ snapshot })}
                    >
                      <Text variant="text4" fontWeight={500}>
                        Download ZIP
                      </Text>
                    </Button>
                  </span>
                </Tooltip>
                {showDeleteAndRenameButtons && (
                  <>
                    <Tooltip title="Rename snapshot">
                      <IconButton
                        onClick={() =>
                          constRenameDialogOpenHandle({
                            id: snapshot.id,
                            name: snapshot.name,
                          })
                        }
                      >
                        <ModeEditOutlineOutlinedIcon
                          sx={{ height: "24px", width: "24px" }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete snapshot">
                      <IconButton
                        onClick={() =>
                          handleDelete({ snapshotId: snapshot.id })
                        }
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{ height: "24px", width: "24px" }}
                        />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>

              <MuiDivider sx={{ gridColumn: "span 3" }} />
            </React.Fragment>
          ))}
        </Stack>
      </Stack>
      <SnapshotRenameDialog
        snapshot={currentSnapshotToEdit}
        open={!_.isNil(currentSnapshotToEdit)}
        onClose={constRenameDialogCloseHandle}
        onUpdate={onUpdate}
      />
      <SnapshotCreateDialog
        open={isCreateDialogOpen}
        onClose={constCreateDialogCloseHandle}
        onUpdate={onUpdate}
        navigateToPDFRenderer={onCreate}
        hideDescription={
          _.isNil(userPermission) ||
          (!userPermission.has_write_access && !userPermission.is_full_access)
        }
      />
    </>
  );
};

export default SavedSnapshots;
